import React, { useState, useEffect } from 'react';
import ProjectModal from './ProjectModal';

// Helper function to get correct image URL for deployment
const getImageUrl = (imagePath) => {
  if (!imagePath) return null;
  // Remove leading slash if present and prepend PUBLIC_URL
  const cleanPath = imagePath.startsWith('/') ? imagePath.slice(1) : imagePath;
  return `${process.env.PUBLIC_URL}/${cleanPath}`;
};

const ProjectCard = ({ project }) => {
  const [open, setOpen] = useState(false);
  const [clicked, setClicked] = useState(false);
  const images = project.images && project.images.length > 0 ? project.images : [];
  const [bgUrl, setBgUrl] = useState(getImageUrl(images[0]) || null);

  useEffect(() => {
    setBgUrl(getImageUrl(images[0]) || null);
  }, [images]);

  // preload and verify image, fallback to null on error
  useEffect(() => {
    if (!bgUrl) return;
    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (!cancelled) setBgUrl(bgUrl);
    };
    img.onerror = () => {
      if (!cancelled) setBgUrl(null);
    };
    img.src = bgUrl;
    return () => { cancelled = true; };
  }, [bgUrl]);
  const handleClick = () => {
    // quick pop animation then open modal
    setClicked(true);
    setOpen(true);
    setClicked(false);
  };

  return (
    <>
      <div 
        onClick={handleClick} 
        className={`cursor-pointer bg-gray-900 border border-gray-800 rounded-lg overflow-hidden group transform-gpu will-change-transform transition-shadow transition-transform duration-300 ease-out flex flex-col h-full ${clicked ? 'shadow-2xl' : 'hover:shadow-lg'}`}
        style={{
          transform: clicked ? 'scale(1.05)' : undefined
        }}
        onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.03)'}
        onMouseLeave={(e) => e.currentTarget.style.transform = clicked ? 'scale(1.05)' : 'scale(1)'}
      >
        <div className="h-44 md:h-52 bg-gray-800 overflow-hidden relative flex-shrink-0">
          {bgUrl ? (
            <div
              className="w-full h-full relative bg-center bg-cover bg-no-repeat"
              style={{ backgroundImage: `url(${bgUrl})` }}
            >
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  <button className="px-3 py-1.5 bg-blue-400 text-black rounded-md font-semibold text-sm">LEARN MORE</button>
                </div>
              </div>
            </div>
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-blue-900 to-gray-800 flex items-center justify-center">
              <div className="text-5xl text-blue-400 opacity-50">🚁</div>
            </div>
          )}
        </div>
        <div className="p-4 flex flex-col flex-1 min-h-0">
          <div className="flex justify-between items-start mb-2">
            <h3 className="text-lg font-bold text-white tracking-wide">{project.title}</h3>
          </div>
          <p className="text-gray-300 mb-3 leading-relaxed text-sm line-clamp-2 overflow-hidden"
             style={{
               display: '-webkit-box',
               WebkitLineClamp: 2,
               WebkitBoxOrient: 'vertical',
               overflow: 'hidden'
             }}>
            {project.description}
          </p>
          <div className="flex flex-wrap gap-1.5 mb-3 flex-grow">
            { (project.tags || []).slice(0, 3).map(tag => (
              <span key={tag} className="px-2 py-1 bg-gray-700 text-gray-300 text-xs font-medium border border-gray-600 h-fit">{tag}</span>
            ))}
            { (project.tags || []).length > 3 && (
              <span className="px-2 py-1 bg-gray-700 text-gray-300 text-xs font-medium border border-gray-600 h-fit">+{(project.tags || []).length - 3}</span>
            )}
          </div>
          <div className="flex justify-between items-center text-sm border-t border-gray-700 pt-2 mt-auto">
            <span className="text-gray-400 font-medium">
              {project.startDate ? (
                project.endDate ? (
                  project.endDate === 'Present' 
                    ? `${new Date(project.startDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', year: 'numeric' })} - Present`
                    : project.startDate === project.endDate 
                      ? new Date(project.startDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
                      : `${new Date(project.startDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', year: 'numeric' })} - ${new Date(project.endDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`
                ) : new Date(project.startDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
              ) : (project.month ? `${project.month} ${project.year}` : project.year)}
            </span>
            {images.length > 1 && (<span className="text-blue-400">{images.length} images</span>)}
          </div>
        </div>
      </div>
      {open && <ProjectModal project={project} onClose={() => setOpen(false)} />}
    </>
  );
};

export default ProjectCard;
